import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useMemo, useRef } from "react";

import { getMessageRecoveryStatus } from "@/api/chat/chat";
import type { IChatRecoveryStatus } from "@/api/chat/chat.types";

import type { TurnAttachmentStatusKind } from "./useTurnAttachment";

/** Cadence while the turn is only queued -- admission is quick, so look often. */
export const RECOVERY_POLL_QUEUED_MS = 2000;
/** Cadence while the model is generating or the turn is finalizing. */
export const RECOVERY_POLL_ACTIVE_MS = 5000;
/** Cadence after the UX-only active budget is spent. Never a terminal. */
export const RECOVERY_POLL_SLOW_MS = 30_000;
/** Ceiling for the error-branch exponential backoff. */
export const RECOVERY_MAX_ERROR_INTERVAL_MS = 5 * 60_000;
/**
 * Successful active polls served at the high-frequency cadence before the UI
 * degrades to slow polling and says "Taking longer than expected". This is a
 * presentation budget only -- the correctness horizon is the server's
 * `recovery_expires_at`, surfaced through `retry_allowed`.
 */
export const RECOVERY_ACTIVE_POLL_BUDGET = 24;

const ACTIVE_STATUSES = new Set(["queued", "generating", "finalizing"]);

export type RecoveryPollState = {
    status: "pending" | "error" | "success";
    error: unknown;
    data: IChatRecoveryStatus | undefined;
    errorUpdateCount: number;
    dataUpdateCount: number;
};

export type MessageRecoveryStatus = {
    data: IChatRecoveryStatus | undefined;
    takingLonger: boolean;
    retryAllowed: boolean;
    statusKind: TurnAttachmentStatusKind;
    /**
     * Monotonic count of reads the server actually answered, 200 and 304 alike.
     * A failed poll does not advance it: a failure is no evidence the turn is
     * still the one being watched.
     */
    successfulReadToken: number;
};

export type UseMessageRecoveryStatusArgs = {
    conversationId: number;
    messageId: number;
    enabled: boolean;
    /** Any terminal, including failures -- the row must be re-read either way. */
    onTerminal?: (status: IChatRecoveryStatus) => void;
    /** Completed only. Never runs for a failed or superseded turn. */
    onCompleted?: (status: IChatRecoveryStatus) => void;
    onGone?: () => void;
};

export const recoveryStatusQueryKey = (
    conversationId: number,
    messageId: number
) => ["chat-recovery-status", conversationId, messageId] as const;

/** The turn genuinely ended. Distinct from having merely stopped being trusted. */
export function isRecoveryTerminal(status: IChatRecoveryStatus): boolean {
    return !ACTIVE_STATUSES.has(status.status);
}

/**
 * The server's correctness horizon has passed while the execution is still
 * active. The server signals it by flipping `retry_allowed` on an active status;
 * the client never compares `recovery_expires_at` against a local clock.
 *
 * Not a terminal: the row is still projected as recovering, so there is no
 * server-side error rendering to hand the message back to. Treating the two as
 * one leaves the user staring at a permanent spinner with no way forward.
 */
export function hasReachedHorizon(status: IChatRecoveryStatus): boolean {
    return (
        status.waiting_for_documents !== true &&
        ACTIVE_STATUSES.has(status.status) &&
        status.retry_allowed
    );
}

/**
 * The one classification of a polled turn, for every consumer that needs one.
 *
 * Built from the predicates above rather than from `status.status` directly, so
 * "what counts as active" is defined once.
 */
export function recoveryStatusKind(
    status: IChatRecoveryStatus | undefined,
    gone: boolean
): TurnAttachmentStatusKind {
    if (gone) return "gone";
    if (status === undefined) return "pending";
    if (isRecoveryTerminal(status)) return "terminal";
    if (hasReachedHorizon(status)) return "horizon";
    return "active";
}

/** Polling stops for a real terminal and at the horizon alike. */
export function shouldStopPolling(status: IChatRecoveryStatus): boolean {
    return isRecoveryTerminal(status) || hasReachedHorizon(status);
}

export function shouldExposeRetry(status: IChatRecoveryStatus): boolean {
    if (
        status.status === "completed" ||
        status.waiting_for_documents === true
    ) {
        return false;
    }
    return status.retry_allowed;
}

/** 404/410 mean the polled row is gone for good; anything else is transient. */
export function isRecoveryStoppingError(error: unknown): boolean {
    if (!axios.isAxiosError(error)) return false;
    const code = error.response?.status;
    return code === 404 || code === 410;
}

export function recoveryPollInterval(
    state: RecoveryPollState,
    consecutiveErrors: number,
    activePolls: number
): number | false {
    if (state.status === "error") {
        if (isRecoveryStoppingError(state.error)) return false;
        const backoff =
            RECOVERY_POLL_ACTIVE_MS * 2 ** Math.max(0, consecutiveErrors - 1);
        return Math.min(backoff, RECOVERY_MAX_ERROR_INTERVAL_MS);
    }
    const status = state.data;
    if (status === undefined) return RECOVERY_POLL_ACTIVE_MS;
    if (shouldStopPolling(status)) return false;
    if (activePolls > RECOVERY_ACTIVE_POLL_BUDGET) return RECOVERY_POLL_SLOW_MS;
    return status.status === "queued"
        ? RECOVERY_POLL_QUEUED_MS
        : RECOVERY_POLL_ACTIVE_MS;
}

/**
 * React Query resets `fetchFailureCount` at the start of every fetch when
 * `retry: false`, so a backoff keyed on it never backs off. `errorUpdateCount`
 * is monotonic, so consecutive failures are its distance from the value held at
 * the last non-error state; the returned closure carries that baseline across
 * poll cycles.
 *
 * Both counters must come from monotonic query state rather than from
 * increments inside this closure: React Query calls `refetchInterval` on every
 * render, not once per poll, so a self-incrementing counter would spend the
 * active budget at a rate set by render churn.
 */
export function makeRecoveryPollInterval(): (
    state: RecoveryPollState
) => number | false {
    let errorBaseline = 0;
    return (state: RecoveryPollState): number | false => {
        if (state.status !== "error") {
            errorBaseline = state.errorUpdateCount;
        }
        const consecutiveErrors = Math.max(
            0,
            state.errorUpdateCount - errorBaseline
        );
        return recoveryPollInterval(
            state,
            consecutiveErrors,
            state.dataUpdateCount
        );
    };
}

export function useMessageRecoveryStatus({
    conversationId,
    messageId,
    enabled,
    onTerminal,
    onCompleted,
    onGone,
}: UseMessageRecoveryStatusArgs): MessageRecoveryStatus {
    const resource = useMemo(
        () => ({
            queryKey: recoveryStatusQueryKey(conversationId, messageId),
            pollInterval: makeRecoveryPollInterval(),
            etag: null as string | null,
            held: undefined as IChatRecoveryStatus | undefined,
            terminalKey: null as string | null,
            gone: false,
            pollsServed: 0,
        }),
        [conversationId, messageId]
    );

    const completedHandler = useRef(onCompleted);
    completedHandler.current = onCompleted;
    const terminalHandler = useRef(onTerminal);
    terminalHandler.current = onTerminal;
    const goneHandler = useRef(onGone);
    goneHandler.current = onGone;

    const query = useQuery({
        queryKey: resource.queryKey,
        queryFn: async () => {
            const read = await getMessageRecoveryStatus(
                conversationId,
                messageId,
                resource.etag
            );
            if (read.etag) resource.etag = read.etag;
            // A 304 carries no body; the held value is still current.
            if (read.status) resource.held = read.status;
            resource.pollsServed += 1;
            return resource.held;
        },
        enabled,
        retry: false,
        refetchInterval: (query) => resource.pollInterval(query.state),
        refetchOnWindowFocus: true,
    });

    const data = query.data;

    useEffect(() => {
        if (data === undefined) return;
        if (!isRecoveryTerminal(data)) return;
        // Dedupe by (message, status_version) so repeats and other tabs cannot
        // fire a terminal handler twice for one turn.
        const key = `${messageId}:${data.status_version}`;
        if (resource.terminalKey === key) return;
        resource.terminalKey = key;
        // Every real terminal needs the row re-read, not just a completed one: a
        // failed or superseded turn still has to replace the recovering
        // rendering with the server's terminal projection. The horizon is
        // excluded -- the row is still projected as recovering there, so a
        // re-read would fetch the same thing and change nothing.
        terminalHandler.current?.(data);
        if (data.status === "completed") {
            completedHandler.current?.(data);
        }
    }, [data, messageId, query.dataUpdatedAt, resource]);

    useEffect(() => {
        if (!isRecoveryStoppingError(query.error)) return;
        if (resource.gone) return;
        resource.gone = true;
        goneHandler.current?.();
    }, [query.error, resource]);

    const statusKind = recoveryStatusKind(
        data,
        resource.gone || isRecoveryStoppingError(query.error)
    );

    return {
        data,
        takingLonger: resource.pollsServed > RECOVERY_ACTIVE_POLL_BUDGET,
        retryAllowed: data === undefined ? false : shouldExposeRetry(data),
        statusKind,
        successfulReadToken: resource.pollsServed,
    };
}
